import { useRef, type MouseEvent, type ReactNode } from 'react';

const VARIANT_CLASSES = {
  primary:
    'border border-primary-800 bg-primary-800 text-mist hover:border-primary-700 hover:bg-primary-700 active:bg-primary-900',
  secondary:
    'border border-dawn bg-dawn text-stage hover:border-accent-300 hover:bg-accent-300 active:bg-accent-500',
  ghost:
    'border border-neutral-300 bg-transparent text-ink hover:border-neutral-400 hover:bg-neutral-100 active:bg-neutral-200',
  light:
    'border border-white/35 bg-transparent text-mist hover:border-white/60 hover:bg-white/10 active:bg-white/20',
} as const;

export function MagneticCta({ href, onClick, children, className = '', variant = 'primary' }: Props) {
  const buttonRef = useRef<HTMLAnchorElement>(null);

  /**
   * The offset is written straight to the element rather than held in state.
   * A pointer move is a per-frame event, and routing it through React ran a
   * full render of this component to produce two numbers that only ever land
   * in an inline transform.
   */
  const handleMouseMove = (event: MouseEvent<HTMLAnchorElement>) => {
    const element = buttonRef.current;

    if (!element) {
      return;
    }

    const rect = element.getBoundingClientRect();
    const x = (event.clientX - rect.left - rect.width / 2) * 0.14;
    const y = (event.clientY - rect.top - rect.height / 2) * 0.14;

    element.style.transform = `translate3d(${x.toFixed(2)}px, ${y.toFixed(2)}px, 0)`;
  };

  const handleMouseLeave = () => {
    const element = buttonRef.current;

    if (element) {
      element.style.transform = 'translate3d(0, 0, 0)';
    }
  };

  return (
    <a
      ref={buttonRef}
      href={href}
      onClick={onClick}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      className={`inline-flex min-h-12 items-center justify-center gap-2 rounded-md px-7 py-3.5 font-body text-sm font-semibold tracking-wide transition-[transform,background-color,border-color] duration-200 ease-in-out ${VARIANT_CLASSES[variant]} ${className}`}
      style={{ transform: 'translate3d(0, 0, 0)' }}
    >
      {children}
    </a>
  );
}

interface Props {
  href?: string;
  onClick?: () => void;
  children: ReactNode;
  className?: string;
  variant?: 'primary' | 'secondary' | 'ghost' | 'light';
}
